import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight, Flame } from "lucide-react";

function chunkItems(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function VegMark({ veg }) {
  return (
    <span
      className={`inline-flex w-3 h-3 items-center justify-center border rounded-[2px] shrink-0 mt-1 ${
        veg ? "border-green-700" : "border-red-700"
      }`}
      title={veg ? "Vegetarian" : "Non-vegetarian"}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          veg ? "bg-green-700" : "bg-red-700"
        }`}
      ></span>
    </span>
  );
}

function SpiceMark({ level }) {
  if (!level) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 align-middle"
      title={level === 2 ? "Extra spicy" : "Medium spicy"}
    >
      {Array.from({ length: level }).map((_, i) => (
        <Flame
          key={i}
          size={12}
          className={level === 2 ? "text-red-600" : "text-orange-500"}
          fill="currentColor"
        />
      ))}
    </span>
  );
}

const PageShell = forwardRef(function PageShell(
  { children, density = "soft", testId },
  ref
) {
  return (
    <div ref={ref} data-density={density} className="menu-page" data-testid={testId}>
      <div className="menu-page-inner">{children}</div>
    </div>
  );
});

function CoverPage() {
  return (
    <div className="mp-cover">
      <div className="mp-cover-frame">
        <div className="mp-cover-dome">A</div>
        <p className="mp-cover-eyebrow">The Finest Authentic Restaurant</p>
        <h3 className="mp-cover-title">AL-HAMEEDIYA</h3>
        <p className="mp-cover-sub">— The Menu —</p>
        <p className="mp-cover-tag">Good Food • Great Taste Always</p>
        <p className="mp-cover-loc">Nagpur, Maharashtra</p>
      </div>
    </div>
  );
}

function BackCover({ info }) {
  return (
    <div className="mp-cover">
      <div className="mp-cover-frame">
        <p className="mp-cover-eyebrow">Visit Us</p>
        <h3 className="mp-cover-title mp-cover-title-sm">AL-HAMEEDIYA</h3>
        <p className="mp-cover-addr">
          {info?.address_line || "Manyawada Square, Tukduji Road, Nagpur"}
        </p>
        <p className="mp-cover-hours">
          {info?.hours || "Open Daily · 12:00 PM – 10:30 PM"}
        </p>
        <p className="mp-cover-tag">Good Food • Great Taste Always</p>
      </div>
    </div>
  );
}

function ListPage({ page }) {
  const { cat, group, items, part, parts } = page;
  return (
    <>
      <div className="mp-head">
        <span className="mp-eyebrow">{cat.eyebrow}</span>
        <div className="mp-plaque">
          {group.name}
          {parts > 1 && (
            <span className="mp-continued">
              {part > 1 ? `(continued)` : ""}
            </span>
          )}
        </div>
        {group.columns && (
          <span className="mp-cols">{group.columns.join(" / ")}</span>
        )}
      </div>
      <ul className="mp-list">
        {items.map((item) => (
          <li
            key={item.name}
            data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="mp-item"
          >
            <VegMark veg={item.veg} />
            <span className="mp-name-wrap">
              <span className="mp-name">
                {item.name} <SpiceMark level={item.spice} />
              </span>
              {item.meta && <span className="mp-meta">{item.meta}</span>}
            </span>
            <span aria-hidden="true" className="mp-dots"></span>
            <span className="mp-price">{item.prices.join(" / ")}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

export default function MenuBook({ categories, info, apiRef }) {
  const bookRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [viewport, setViewport] = useState({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  });

  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const portrait = viewport.w < 820;
  // Page size: full width on desktop, fitted to the phone screen on mobile
  const pageW = portrait
    ? Math.max(280, Math.min(440, viewport.w - 84))
    : 440;
  const pageH = portrait
    ? Math.min(Math.round(pageW * 1.45), viewport.h - 300)
    : Math.round((pageW * 560) / 440);
  const perPage = portrait ? 6 : 9;
  // Frame is page height + ~40px padding/border. Scale the whole book
  // down on shorter screens so a full page is always visible without scroll.
  const FRAME_H = pageH + 40;
  const scale = Math.min(1, (viewport.h - 285) / FRAME_H);

  const { pages, categoryStart } = useMemo(() => {
    const pages = [];
    const categoryStart = {};
    for (const cat of categories) {
      for (const group of cat.groups) {
        const chunks = chunkItems(group.items, perPage);
        chunks.forEach((items, i) => {
          if (!(cat.slug in categoryStart)) {
            categoryStart[cat.slug] = pages.length + 1; // +1 for the cover
          }
          pages.push({ cat, group, items, part: i + 1, parts: chunks.length });
        });
      }
    }
    return { pages, categoryStart };
  }, [categories, perPage]);

  useImperativeHandle(apiRef, () => ({
    flipToCategory(slug) {
      const idx = categoryStart[slug];
      if (idx != null && bookRef.current) {
        bookRef.current.pageFlip().flip(idx);
      }
    },
  }));

  // Reset to the cover when filters change the page list
  useEffect(() => {
    setCurrent(0);
    if (bookRef.current) {
      try {
        bookRef.current.pageFlip().turnToPage(0);
      } catch (e) {
        /* book not mounted yet */
      }
    }
  }, [pages.length]);

  const total = pages.length + 2; // cover + back cover

  return (
    <div data-testid="menu-book" className="flex flex-col items-center">
      <div
        className="menu-book-frame relative"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top center",
          marginBottom: `${(scale - 1) * FRAME_H}px`,
        }}
      >
        <button
          onClick={() => bookRef.current?.pageFlip().flipPrev()}
          disabled={current === 0}
          data-testid="book-prev-btn"
          aria-label="Previous page"
          className="book-arrow absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 z-20"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => bookRef.current?.pageFlip().flipNext()}
          disabled={current >= total - 1}
          data-testid="book-next-btn"
          aria-label="Next page"
          className="book-arrow absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 z-20"
        >
          <ChevronRight size={20} />
        </button>
        <HTMLFlipBook
          key={`book-${pages.length}-${portrait}`}
          ref={bookRef}
          width={pageW}
          height={pageH}
          size="stretch"
          minWidth={260}
          maxWidth={pageW}
          minHeight={320}
          maxHeight={pageH}
          maxShadowOpacity={0.45}
          showCover={true}
          usePortrait={portrait}
          mobileScrollSupport={true}
          className="menu-flipbook"
          onFlip={(e) => setCurrent(e.data)}
        >
          <PageShell density="hard" testId="book-cover">
            <CoverPage />
          </PageShell>
          {pages.map((page, i) => (
            <PageShell
              key={`${page.group.name}-${page.part}`}
              testId={`book-page-${i + 1}`}
            >
              <ListPage page={page} />
              <span className="mp-pagenum">
                {i + 1} · {page.cat.title}
              </span>
            </PageShell>
          ))}
          <PageShell density="hard" testId="book-back-cover">
            <BackCover info={info} />
          </PageShell>
        </HTMLFlipBook>
        <span
          data-testid="book-page-indicator"
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full bg-[#0B1319]/85 border border-[#D4AF37]/40 text-[10px] text-[#F7D774] tracking-widest uppercase"
        >
          Page {current + 1} of {total}
        </span>
      </div>
    </div>
  );
}
